/**
 * The Sales Tax Rate Table's own vocabulary (piece 25; decisions 0219, 0426):
 * the states with no statewide sales tax, the explicit zero-rate label, the
 * closed lists the table's two enumerated columns take, and the refusals the
 * editor answers with. 🔨 The table never refreshes itself from a live
 * source; Check for Updates prompts a manual review and writes no rate.
 */
import { NO_SALES_TAX_STATES } from '../records/taxExemption';
import { formatPercent } from '../money';
import { counted } from '../composites/countedUnit';

export const USE_TAX_ON_LOCAL_VALUES = ['Yes', 'No', 'Partial'] as const;
export type UseTaxOnLocalValue = (typeof USE_TAX_ON_LOCAL_VALUES)[number];

export const SALES_TAX_BASES = ['Accrual', 'Cash', 'AccrualWithBadDebtCredit'] as const;
export type SalesTaxBasisValue = (typeof SALES_TAX_BASES)[number];
/** 🔨 Never "same as income": income tax basis is a federal election, sales tax basis is each state's own rule (piece 25). */
export const DEFAULT_SALES_TAX_BASIS: SalesTaxBasisValue = 'Accrual';

export const SALES_TAX_BASIS_LABELS: Readonly<Record<SalesTaxBasisValue, string>> = {
  Accrual: 'Accrual — due when the invoice is issued',
  Cash: 'Cash — due when the invoice is paid',
  AccrualWithBadDebtCredit: 'Accrual, with a bad-debt credit on a write-off',
};

export const FILING_FREQUENCIES = ['Monthly', 'Quarterly', 'Semiannual', 'Annual'] as const;
export type FilingFrequencyValue = (typeof FILING_FREQUENCIES)[number];

/** How many months one return period covers, which is what the generator cuts periods from. */
export const FILING_PERIOD_MONTHS: Readonly<Record<FilingFrequencyValue, number>> = {
  Monthly: 1,
  Quarterly: 3,
  Semiannual: 6,
  Annual: 12,
};

/**
 * The five states with no statewide sales tax are `NO_SALES_TAX_STATES`,
 * already declared beside the exemption forms. Alaska is among them because it
 * levies none at state level, and its local jurisdictions are exactly the
 * variation the documented Local Range exists to carry rather than model.
 */
export function hasNoStatewideSalesTax(stateCode: string): boolean {
  return NO_SALES_TAX_STATES.includes(stateCode.toUpperCase());
}

/** A tax rate reads to three decimal places at most, trimmed, so 6.0 and 6.000 print as the same 6% wherever a rate is drawn (T660). */
export function formatRatePercent(ratePercent: number): string {
  return formatPercent(ratePercent, { maxDecimals: 3 });
}

/**
 * 🔒 A state with no statewide sales tax shows the explicit zero-rate label
 * rather than an empty rate (piece 25). An empty cell reads as a rate nobody
 * has entered, which is a different and much worse fact.
 */
export const NO_SALES_TAX_LABEL = '0.000% — no sales tax';

export function rateLabel(stateCode: string, ratePercent: number | null): string {
  if (hasNoStatewideSalesTax(stateCode)) return NO_SALES_TAX_LABEL;
  if (ratePercent === null) return 'no rate entered';
  return formatRatePercent(ratePercent);
}

/** What Check for Updates answers with: a prompt to review, never a written rate. */
export function checkForUpdatesPrompt(stateCount: number, oldestVerifiedOn: string | null): string {
  const age = oldestVerifiedOn ? `the oldest was last verified on ${oldestVerifiedOn}` : 'none carries a verified date';
  return `Review ${counted(stateCount, 'state rate')} against each state's own published figure; ${age}. Nothing here is refreshed automatically, so a rate changes only when somebody enters it.`;
}

/** A rate the editor refuses by name, so a mistyped figure never reaches a customer's invoice. */
export function ratePercentProblem(value: unknown): string | null {
  const rate = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(rate)) return 'a rate is a number of percent, such as 6 for six percent';
  if (rate < 0) return 'a rate is never negative';
  if (rate > 25) return 'a rate above 25 percent is refused; no United States state levies one, so this is a typing error';
  return null;
}

export function useTaxOnLocalProblem(value: unknown): string | null {
  return (USE_TAX_ON_LOCAL_VALUES as readonly unknown[]).includes(value) ? null : `use tax on local is one of ${USE_TAX_ON_LOCAL_VALUES.join(', ')}`;
}
